"use client";
import type { JSX } from "react";
import { useState } from "react";
import { motion, type Variants } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/home-v3/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CTA_GRADIENT_BACKGROUND_CLASSNAME } from "@/lib/cta-styles";

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const faqItems = [
  {
    question: "Can a foreigner register a company in Hong Kong?",
    answer:
      "Yes. A director does not have to be a Hong Kong resident. A private Hong Kong company must have at least one individual director and a company secretary that meets Hong Kong requirements.",
  },
  {
    question: "Can I register a Hong Kong company remotely?",
    answer:
      "Yes. Hong Kong supports electronic company incorporation, and MuteTaxes can manage the process with you remotely.",
  },
  {
    question: "Do I need a Hong Kong address to form a company?",
    answer:
      "Yes. A local limited company's registered office must be located in Hong Kong. Your registered office details are included in the incorporation filing.",
  },
  {
    question: "Do I need a company secretary in Hong Kong?",
    answer:
      "Yes. Every Hong Kong private company must appoint a company secretary. If the secretary is an individual, they must ordinarily reside in Hong Kong. A corporate company secretary must have a registered or principal office in Hong Kong.",
  },
  {
    question: "Can I own 100% of my Hong Kong company?",
    answer:
      "A foreign founder can be a shareholder and director of a Hong Kong private company. Your specific shareholding and company structure should be confirmed during onboarding based on your setup.",
  },
  {
    question: "Is there a minimum share capital requirement?",
    answer:
      "Hong Kong's Companies Ordinance does not prescribe a minimum amount of paid-up capital for company incorporation.",
  },
  {
    question: "What documents are required to register a Hong Kong company?",
    answer:
      "The exact information depends on your company structure. The official incorporation process generally requires an incorporation form, Articles of Association, business registration documentation, and information about the company's directors, company secretary and registered office.",
  },
  {
    question: "Do I receive a Business Registration Certificate?",
    answer:
      "Yes. Under Hong Kong's one-stop registration system, an approved local company incorporation can receive its Certificate of Incorporation and Business Registration Certificate together.",
  },
  {
    question: "Can MuteTaxes also help with a Hong Kong business bank account?",
    answer:
      "Yes. Banking assistance is included depending on the package you select. The Starter Shield currently includes an Airwallex account, while the higher packages add more banking and payment options.",
  },
  {
    question: "Can MuteTaxes help with PayPal and other payment processors?",
    answer:
      "Yes. The Fortress and Titanium packages currently include additional payment services, with the exact inclusions shown in the pricing section above.",
  },
  {
    question: "How long does Hong Kong company formation take?",
    answer:
      "The government incorporation portion can move quickly once all required information and documents are ready. Your complete MuteTaxes timeline also depends on the banking, payment accounts and services included in your package. The current Starter Shield is advertised with a seven-day setup target.",
  },
  {
    question: "What happens after my company is registered?",
    answer:
      "Once incorporation is completed, we proceed with the banking and payment portions included in your package. Hong Kong companies also have ongoing statutory filing obligations after incorporation, so company formation should be treated as the start of your compliance responsibilities rather than the end.",
  },
];

const INITIAL_VISIBLE_COUNT = 8;

export const FrequentlyAskedQuestionsSection = (): JSX.Element => {
 const [showAll, setShowAll] = useState(false);
  const visibleFaqItems = showAll
    ? faqItems
    : faqItems.slice(0, INITIAL_VISIBLE_COUNT);

  return (
    <section
      aria-labelledby="faq-heading"
      className="relative flex w-full flex-col max-sm:mt-20 items-center gap-14 px-4 pt-6 sm:px-6"
    >
      <header className="flex w-full flex-col items-center gap-6">
        <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px] backdrop-brightness-100">
          Have questions?
        </Badge>
        <motion.h2
          id="faq-heading"
          className="[font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-4xl font-normal leading-[1.15] tracking-[-1.92px] text-white sm:text-5xl sm:leading-[68px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
          Find quick answers below.
        </motion.h2>
      </header>
      <div className="flex w-full max-w-[800px] flex-col">
        <Accordion
          type="single"
          collapsible
          className="flex w-full flex-col gap-[13px]"
        >
          {visibleFaqItems.map(({ question, answer }, index) => (
            <AccordionItem
              key={question}
              value={`faq-${index + 1}`}
              className="relative overflow-hidden rounded-[20px] border border-[#ffffff29] bg-[#0f1218]"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -top-[138px] left-[calc(50%+240px)] h-[171px] w-[171px] rounded-full bg-[#6fa6ff] blur-[71.73px]"
              />
              <AccordionTrigger className="relative z-10 flex min-h-[80px] w-full items-center justify-between gap-4 px-6 max-sm:py-3 py-6 text-left no-underline hover:no-underline [&>svg]:h-8 [&>svg]:w-8 [&>svg]:shrink-0 [&>svg]:text-white">
                <span className="[font-family:'Suisse_Intl_Trial-Medium',Helvetica] max-sm:text-base text-xl font-medium leading-7 tracking-[0] text-white">
                  {question}
                </span>
              </AccordionTrigger>
              <AccordionContent className="relative z-10 px-6 pb-6 pt-0 [font-family:'Suisse_Intl_Trial-Regular',Helvetica] max-sm:text-sm text-base font-normal leading-6 text-[#ffffffb2]">
                {answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        {faqItems.length > INITIAL_VISIBLE_COUNT && (
          <div className="flex justify-center pt-14">
            <Button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              className={`h-auto gap-2 rounded-3xl px-6 py-4 font-inter text-sm font-medium leading-4 text-white ${CTA_GRADIENT_BACKGROUND_CLASSNAME}`}
            >
              {showAll ? "Show Less" : "Show More"}
              <img
                className={`h-4 w-4 transition-transform ${showAll ? "rotate-180" : ""}`}
                alt=""
                aria-hidden="true"
                src="/home-v3/component-1-3.svg"
              />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
